/*    
<summary>
  This functional component "LogoutPopup" provides modal popup that shows logout confirmation message (i.e. Are you sure 
  you want logout?).
  Developer:Aashish Singh, Created Date:28-Aug-2022, Last Updated By: Mohammad Saquib Khan, Updated Date: 26-Oct-2022
</summary>
<returns>Returns JSX</returns>
*/

import { observer } from "mobx-react";
import { useEffect } from "react";
import { Button, Modal } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useStore } from "../../../contexts/store-provider";
import storeManagementStore from "../../../core/stores/store-management-store";
import RoutesConstants from "../../../shared-components/routes/routes-constants";
import { formatMessage } from "../../../translations/format-message";
import "./logout-popup.css";
import userTypeEnum from "../../../constants/enums/user-type-enum";
import { getUserTypeFromLocalStorage } from "../../../helpers/localstorage-helper";

interface IProps {
  modalClosed: () => void;
}

// LogoutPopup component displays a modal for logout confirmation
const LogoutPopup = (props: IProps) => {
  const history = useHistory();
  const location = useLocation();
  const { authStore, userStore, preferencesStore } = useStore();
  const { logout, logoutError, logoutSuccess, logoutInprogress, resetLogout } =
    authStore;

  // Reset all relevant stores after logout
  const resetStores = () => {
    userStore.reset();
    authStore.reset();
    storeManagementStore.resetStore();
  };

  // Show error toast if logout fails
  useEffect(() => {
    if (logoutError) {
      toast.error(formatMessage(logoutError));
      resetLogout();
    }
  }, [logoutError]);

  // On successful logout, show success toast, reset stores, and redirect
  useEffect(() => {
    if (logoutSuccess) {
      resetLogout();
      let userType = getUserTypeFromLocalStorage();
      switch(userType){
        case userTypeEnum[userTypeEnum.SuperAdmin]:
          history.push(RoutesConstants.AdminLogin);
          break;
        case userTypeEnum[userTypeEnum.Customer]:
          history.push(RoutesConstants.CustomerLogin);
          break;
        case userTypeEnum[userTypeEnum.Staff]:
          history.push(RoutesConstants.StaffLogin);
          break;
        case userTypeEnum[userTypeEnum.Store]:
          history.push(RoutesConstants.StoreLogin);
          break;
        case userTypeEnum[userTypeEnum.Group]:
          history.push(RoutesConstants.GroupLogin);
          break;
        case userTypeEnum[userTypeEnum.HeadQuarter]:
          history.push(RoutesConstants.HeadQuarterLogin);
          break;
        default:
          history.push(RoutesConstants.PageNotFound);
      }
      setTimeout(()=>{
        localStorage.removeItem("lizera_userType");
      }, 100);
      props.modalClosed();
      resetStores();
    }
  }, [logoutSuccess]);

  // Call logout function from authStore
  const logoutCaller = () => {
    authStore.logout();
    // setUpdateTheme("false");
  };

  return (
    <Modal show={true} centered dialogClassName="no-data-found-popup">
      <Modal.Header>
        <Modal.Title>
          {/* Modal title with logout text */}
          <FormattedMessage id="logout" />
          {/* Close button */}
          <button onClick={props.modalClosed} className="Crossicons">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="23"
              height="23"
              fill="currentColor"
              className="bi bi-x"
              viewBox="0 0 16 16"
            >
              <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
            </svg>
          </button>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="bootbox-body p-3">
          {/* Logout icon */}
          <div className="logout_modal_icon">
            <svg
              width="50"
              height="50"
              fill="#fff"
              id="_レイヤー_6"
              data-name="レイヤー 6"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 30 30"
            >
              <path d="M20.15,26.04H9.41c-1.9,0-3.45-1.55-3.45-3.45V7.41c0-1.9,1.55-3.45,3.45-3.45h10.74c1.9,0,3.45,1.55,3.45,3.45v3.01h-1.6v-3.01c0-1.02-.83-1.85-1.85-1.85H9.41c-1.02,0-1.85,.83-1.85,1.85v15.18c0,1.02,.83,1.85,1.85,1.85h10.74c1.02,0,1.85-.83,1.85-1.85v-3.37h1.6v3.37c0,1.9-1.55,3.45-3.45,3.45Z" />
              <rect x="14.27" y="13.64" width="8.89" height="2.73" />
              <polygon points="27.04 14.81 23.48 16.74 19.93 18.68 19.93 14.81 19.93 10.94 23.48 12.87 27.04 14.81" />
            </svg>
          </div>
          {/* Logout confirmation message */}
          <div className="mt-3">
            <FormattedMessage id="logout_message" />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        {/* Close button */}
        <Button
          variant="outline-danger"
          onClick={props.modalClosed}
          className="btnOkSize"
        >
          <i className="bi bi-x-lg me-1"></i>
          <FormattedMessage id="close" />
        </Button>
        {/* Logout confirmation button */}
        <Button variant="primary" onClick={logoutCaller} className="btnOkSize">
          <FormattedMessage id="logout" />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default observer(LogoutPopup);
